import { Theme } from '@src/types/enum.js';
import { LitElement } from 'lit';
import { createCustomerAttributeInteraction } from '@common/customerAttributes/createCustomerAttributeInteraction.js';
import { replaceO2SpanWithAirFontLigature } from '@common/utils/dsone/replaceO2SpanWithAirFontLigature.js';
import type { TefComsCustomerAttributeInline } from '@src/components/tef-coms-customer-attribute-inline.js';
import type { TefComsCustomerAttribute } from '@src/components/tef-coms-customer-attribute.js';
import type { TefComs } from '@src/components/tef-coms.js';
import { store } from '@src/store/index.js';

type Constructor<T = object> = new (...args: any[]) => T; // eslint-disable-line @typescript-eslint/no-explicit-any

type ReplaceElementCompatibleTypes = TefComs | TefComsCustomerAttribute | TefComsCustomerAttributeInline;

export declare class ReplaceElementsMixinI {
  replaceElements(targetElement?: HTMLElement): void;
}

/**
 * A mixin that replaces relevant DOM parts with templates rendered using the current theme (occl/tefOne)
 */
export const ReplaceElementsMixin = <T extends Constructor<LitElement>>(superClass: T) => {
  class ReplaceElementsMixinClass extends superClass {
    replaceElements(this: ReplaceElementCompatibleTypes, targetElement: HTMLElement | null = null) {
      const { config: { occluri, stylecontext } } = store.getState();
      const isOcclTheme = !!occluri;
      this.setTemplatingTheme(isOcclTheme ? Theme.OCCL : Theme.TEFONE);

      const target = targetElement || this.shadowRoot || this;

      createCustomerAttributeInteraction(target);
      this.createTooltipInteraction(target, stylecontext!);

      if (!isOcclTheme) {
        replaceO2SpanWithAirFontLigature(target);

        this.replaceElementsWithTpl(target, 'emTemplate', 'em');
        this.replaceElementsWithTpl(target, 'comsP1Template', 'div.coms-paragraph-1');
        this.replaceElementsWithTpl(target, 'comsP2Template', 'div.coms-paragraph-2');
        this.replaceElementsWithTpl(target, 'comsP3Template', 'div.coms-paragraph-3');
        this.replaceElementsWithTpl(target, 'comsText1Template', 'span.coms-text-1');
        this.replaceElementsWithTpl(target, 'comsText2Template', 'span.coms-text-2');
        this.replaceElementsWithTpl(target, 'comsText3Template', 'span.coms-text-3');
        this.replaceElementsWithTpl(target, 'linkTemplate', 'a');
      }
    }
  }
  return ReplaceElementsMixinClass as Constructor<ReplaceElementsMixinI> & T;
};
